import { signIn, signOut, fetchAuthSession, getCurrentUser as amplifyGetCurrentUser, resetPassword, confirmResetPassword, deleteUser, updatePassword, updateUserAttributes, confirmSignUp, resendSignUpCode, signInWithRedirect } from '@aws-amplify/auth';
import {
  IAuthService,
  RedirectSignInOptions,
  RegisterExternalUserParams,
  SignUpParams,
} from '../../../services/auth/AuthService';
import { AuthResult, TokenValidation } from '../../../../shared/types/auth';
import { User } from '../../../../utils/typescript/interfaces';
import { AWSConfig } from '../../../config/BackendConfig';

/**
 * AWS Cognito implementation of the authentication service
 */
export class CognitoAuthService implements IAuthService {
  private config: AWSConfig['auth'];

  constructor(config: AWSConfig['auth']) {
    this.config = config;
  }

  async signIn(email: string, password: string): Promise<AuthResult> {
    try {
      const signInResult = await signIn({
        username: email,
        password,
      });

      // After successful sign in, get the user and tokens
      const user = await this.getCurrentUser();
      if (!user) {
        throw new Error('Failed to get user after sign in');
      }

      const session = await fetchAuthSession();
      const { accessToken, idToken } = session.tokens ?? {};

      if (!accessToken || !idToken) {
        throw new Error('Failed to get tokens after sign in');
      }

      return {
        user,
        accessToken: accessToken.toString(),
        refreshToken: '', // Cognito handles refresh tokens internally
        expiresIn: (accessToken.payload.exp as number) - Math.floor(Date.now() / 1000),
      };
    } catch (error) {
      // Re-throw with consistent error handling
      throw error;
    }
  }

  async signOut(): Promise<void> {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
      throw error;
    }
  }

  async getCurrentUser(): Promise<User | null> {
    try {
      const session = await fetchAuthSession();
      const { idToken } = session.tokens ?? {};
      
      if (!idToken) {
        return null;
      }

      const payload = idToken.payload as any;

      // Map Cognito token payload to our User interface
      return {
        sub: payload.sub,
        email: payload.email,
        name: payload.given_name || payload.name || payload.email, // Ensure name is always provided
        family_name: payload.family_name || '',
        'custom:clientId': payload['custom:clientId'],
      };
    } catch (error) {
      console.error('Get current user error:', error);
      return null;
    }
  }

  async refreshToken(): Promise<string> {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      const { accessToken } = session.tokens ?? {};
      
      if (!accessToken) {
        throw new Error('Failed to refresh token');
      }

      return accessToken.toString();
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  async validateToken(token: string): Promise<TokenValidation> {
    try {
      // For Cognito, we can validate by trying to get current session
      const session = await fetchAuthSession();
      const { accessToken } = session.tokens ?? {};
      
      if (!accessToken || accessToken.toString() !== token) {
        return {
          isValid: false,
          error: 'Token mismatch or expired',
        };
      }

      const payload = accessToken.payload;
      const expiresAt = new Date((payload.exp as number) * 1000);
      
      if (expiresAt < new Date()) {
        return {
          isValid: false,
          error: 'Token expired',
        };
      }

      return {
        isValid: true,
        userId: payload.sub as string,
        clientId: payload['custom:clientId'] as string,
        expiresAt,
      };
    } catch (error) {
      return {
        isValid: false,
        error: (error as Error).message,
      };
    }
  }

  async resetPassword(email: string): Promise<void> {
    try {
      await resetPassword({ username: email });
    } catch (error) {
      console.error('Reset password error:', error);
      throw error;
    }
  }

  async confirmResetPassword(email: string, code: string, newPassword: string): Promise<void> {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
    } catch (error) {
      console.error('Confirm reset password error:', error);
      throw error;
    }
  }

  async deleteUser(): Promise<void> {
    try {
      await deleteUser();
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  }

  async updatePassword(oldPassword: string, newPassword: string): Promise<void> {
    try {
      await updatePassword({
        oldPassword,
        newPassword,
      });
    } catch (error) {
      console.error('Update password error:', error);
      throw error;
    }
  }

  async updateUserAttributes(attributes: Record<string, string>): Promise<void> {
    try {
      await updateUserAttributes({
        userAttributes: attributes,
      });
    } catch (error) {
      console.error('Update user attributes error:', error);
      throw error;
    }
  }

  async signUp(signUpParams: SignUpParams): Promise<{ userId: string; nextStep: any }> {
    try {
      const metadata = signUpParams.options?.clientMetadata ?? {};
      const attributes = signUpParams.options?.userAttributes ?? {};
      await this.postRegister({
        email: signUpParams.username,
        password: signUpParams.password,
        firstName: attributes.given_name || attributes.name,
        lastName: attributes.family_name,
        inviteToken: metadata.link,
        companySlug: metadata.team,
        companyName: metadata.company_name,
        utm: metadata.utm ? JSON.parse(metadata.utm) : undefined,
      });

      return {
        userId: '',
        nextStep: {signUpStep: 'DONE'},
      };
    } catch (error) {
      console.error('Sign up error:', error);
      throw error;
    }
  }

  async registerExternalUser(params: RegisterExternalUserParams): Promise<void> {
    try {
      await this.postRegister({
        ...params,
        externalProvider: true,
      });
    } catch (error) {
      if (error instanceof Error && error.name === 'UsernameExistsException') {
        return;
      }

      throw error;
    }
  }

  private async postRegister(payload: {
    email: string;
    password?: string;
    firstName?: string;
    lastName?: string;
    inviteToken?: string;
    companySlug?: string;
    companyName?: string;
    utm?: unknown;
    externalProvider?: boolean;
  }): Promise<void> {
    const csrfToken = await this.fetchRegisterCsrfToken();
    const response = await fetch(this.getRegisterEndpoint(), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'x-register-csrf-token': csrfToken,
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const responsePayload = await response.json().catch(() => ({}));
      throw this.mapRegisterError(responsePayload.error, responsePayload.message);
    }
  }

  private async fetchRegisterCsrfToken(): Promise<string> {
    const response = await fetch(this.getRegisterCsrfEndpoint(), {
      method: 'GET',
      headers: {
        'content-type': 'application/json',
      },
    });

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw this.mapRegisterError(payload.error, payload.message);
    }

    const payload = await response.json() as {token?: string};
    if (!payload.token) {
      throw new Error('Register CSRF token response is missing token');
    }

    return payload.token;
  }

  private getRegisterEndpoint(): string {
    const pgServerEndpoint = import.meta.env.VITE_APP_PM_SERVER_ENDPOINT;
    if (!pgServerEndpoint) {
      throw new Error('VITE_APP_PM_SERVER_ENDPOINT is not configured');
    }

    const url = new URL(pgServerEndpoint);
    url.pathname = '/register';
    url.search = '';
    url.hash = '';
    return url.toString();
  }

  private getRegisterCsrfEndpoint(): string {
    const url = new URL(this.getRegisterEndpoint());
    url.pathname = '/register/csrf';
    return url.toString();
  }

  private mapRegisterError(code?: string, message?: string): Error {
    const error = new Error(message || code || 'Registration failed');

    switch (code) {
      case 'email_taken':
        error.name = 'UsernameExistsException';
        error.message = 'An account with this email already exists';
        break;
      case 'invite_link_invalid':
        error.name = 'UserLambdaValidationException';
        error.message = 'invite link is invalid or expired';
        break;
      case 'registration_rejected':
        error.name = 'UserLambdaValidationException';
        error.message = 'Registration rejected';
        break;
      case 'csrf_required':
      case 'csrf_invalid':
      case 'csrf_expired':
        error.name = 'ForbiddenException';
        break;
      case 'rate_limited':
        error.name = 'TooManyRequestsException';
        break;
      case 'password_invalid':
        error.name = 'InvalidPasswordException';
        break;
      case 'validation_error':
      case 'password_required':
        error.name = 'InvalidParameterException';
        break;
      default:
        error.name = 'InternalErrorException';
    }

    return error;
  }

  async confirmSignUp(email: string, code: string): Promise<void> {
    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      });
    } catch (error) {
      console.error('Confirm sign up error:', error);
      throw error;
    }
  }

  async resendSignUpCode(email: string): Promise<void> {
    try {
      await resendSignUpCode({
        username: email,
      });
    } catch (error) {
      console.error('Resend sign up code error:', error);
      throw error;
    }
  }

  async signInWithRedirect(provider?: string, options?: RedirectSignInOptions): Promise<void> {
    try {
      await signInWithRedirect({
        provider: provider as any,
        customState: options?.customState,
      });
    } catch (error) {
      console.error('Sign in with redirect error:', error);
      throw error;
    }
  }

  async fetchAuthSession() {
    return fetchAuthSession();
  }

  /**
   * checks if a user seesion valid
   */
  async getCurrentAuthUser() {
    return amplifyGetCurrentUser()
  }
}
